export const numberWithThousandSeparator = (x: number, fixed: number = 2, separator: string = ' ') =>
    x.toFixed(fixed).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

export const formatDate = (d: Date): string => {
    //// Date.getMonth() returns the month number starting from 0 for JAN ////
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

export const formatCurrencyValue = (value: number, units: number) => {
    const formatted = value / units;
    const rounded = Math.floor(value / units);
    let decimals = ((formatted - rounded) * units).toFixed(0);
    if (decimals.length === 1) {
        decimals = '0' + decimals;
    }

    return `${rounded.toLocaleString('en-US').replace(/,/g, ' ')}.${decimals}`;
};

// export const formatBetValue = (value: number): string => {
//     // Handle values >= 1000
//     if (value >= 1000) {
//         const thousands = value / 1000;
//         // Show one decimal place if not a whole K (e.g., 12.5K)
//         if (thousands % 1 !== 0) {
//             return `${thousands.toFixed(1)}K`;
//         }
//         // Show whole number if a whole K (e.g., 12K)
//         return `${thousands.toFixed(0)}K`;
//     }

//     // Handle values < 10000
//     // Check if the number has decimals
//     if (value % 1 !== 0) {
//         // Keep decimals if they are not .00
//         return value.toFixed(2);
//     }
//     // Remove .00 if it's a whole number
//     return value.toFixed(0);
// };

export const formatBetValue = (value: number, limit: number = 4): string => {
    if(value.toString().startsWith('0.0')){
        return value.toString();
    }

    const withDecimals = value.toString().length <= limit ? value.toFixed(2) : value.toString();
    if (withDecimals.length <= limit && value < 1_000) return withDecimals;

    const formatAbbreviation = (num: number): string => {
        if (num >= 1_000_000) {
            const millions = num / 1_000_000;
            return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
        } else if (num >= 1_000) {
            const thousands = num / 1_000;
            return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
        }
        return num.toString();
    };

    const abbreviated = formatAbbreviation(value);
    if (abbreviated.length <= limit || value > 1_000) return abbreviated;
    
    return abbreviated.slice(0, limit);
};

export const formatMinMaxBetsForDisplay = (
    minBet: number,
    maxBet: number,
    maxLength: number = 9 // This maxLength is for minStr.length + maxStr.length
): { formattedMinBet: string; formattedMaxBet: string } => {

    const abbreviateNumber = (num: number): string => {
        if (num >= 1_000_000) {
            const millions = num / 1_000_000;
            return millions.toString().replace(/\.0+$/, '') + 'M';
        }
        if (num >= 1_000) {
            const thousands = num / 1_000;
            if (num % 1000 === 0) { // For exact thousands (e.g., 1000, 2000, ..., 9000, 10000, 11000)
                return `${thousands.toFixed(0)}K`; // "1K", "2K", "10K", "11K"
            }
            // For numbers between 1,000 and 9,999.99... (not exact thousands)
            if (num < 10_000) {
                if (num % 1 === 0) { // Integer
                    if (num % 100 === 0) { // Ends in "00" (e.g., 1200, 1500)
                        return `${thousands.toFixed(1)}K`; // "1.2K", "1.5K"
                    } else { // Does not end in "00" (e.g., 1250, 1995)
                        return num.toString(); // "1250", "1995"
                    }
                } else { // Has decimals (e.g., 1250.50)
                    return `${thousands.toFixed(1)}K`; // "1.3K"
                }
            } else { // For numbers >= 10,000 that are not exact thousands (e.g., 10500, 12345.67)
                return `${thousands.toFixed(1)}K`; // e.g., "10.5K", "12.3K"
            }
        }
        return num.toString(); // For numbers < 1000
    };

    const checkLength = (min: string, max: string): boolean => {
        return (min.length + max.length) <= maxLength;
    };

    // Determine the 'best full display' for minBet (prefer .toFixed(0) for integers, else .toFixed(2))
    const minBetFullDisplay = (minBet % 1 === 0 && !minBet.toString().includes('.')) ? minBet.toFixed(0) : minBet.toFixed(2);
    const minBetAbbreviatedByHelper = abbreviateNumber(minBet);

    // Condition for overriding maxLength: if abbreviating minBet would change its representation significantly (e.g., 2582.56 -> 2.6K)
    // and the abbreviation involves 'K' or 'M'.
    const shouldOverrideForMinBet = minBetFullDisplay !== minBetAbbreviatedByHelper && /[KM]$/.test(minBetAbbreviatedByHelper);

    if (shouldOverrideForMinBet && !minBetFullDisplay.endsWith('00')) {
        // If overriding, display minBet in its "full" form and maxBet in its most abbreviated form, ignoring maxLength for the pair.
        return {
            formattedMinBet: minBetFullDisplay,
            formattedMaxBet: abbreviateNumber(maxBet)
        };
    }

    // Handle edge case for very small bets (e.g. 0.01 - 0.05)
    // Also ensures that if both are < 1, they are shown with 2 decimal places if they fit.
    if (minBet >= 0 && minBet < 1 && maxBet >= 0 && maxBet < 1) {
        const fMinSmall = minBet.toFixed(2);
        const fMaxSmall = maxBet.toFixed(2);
        // No need to check length here, as this is a specific case for small numbers.
        // If they don't fit, the main logic below will try to abbreviate or shorten them.
        // However, for consistency, let's check, or assume they usually fit.
        if (checkLength(fMinSmall, fMaxSmall)) {
             return { formattedMinBet: fMinSmall, formattedMaxBet: fMaxSmall };
        }
        // If they don't fit even like this, fall through to the main logic.
    }

    const minBetFormatGenerators: (() => string)[] = [];
    minBetFormatGenerators.push(() => minBet.toFixed(2));
    if (minBet % 1 !== 0) { // If has decimals and not an integer
      minBetFormatGenerators.push(() => minBet.toFixed(1));
    }
    minBetFormatGenerators.push(() => minBet.toFixed(0));
    if (minBet % 1 === 0) { // If integer, its string form might be shorter (e.g. "10" vs "10.00")
        minBetFormatGenerators.push(() => minBet.toString());
    }
    minBetFormatGenerators.push(() => abbreviateNumber(minBet));

    const maxBetFormatGenerators: (() => string)[] = [];
    maxBetFormatGenerators.push(() => abbreviateNumber(maxBet));
    maxBetFormatGenerators.push(() => maxBet.toFixed(0));
    if (maxBet % 1 === 0) { // If integer
        maxBetFormatGenerators.push(() => maxBet.toString());
    }
    if (maxBet % 1 !== 0) { // If has decimals and not an integer
      maxBetFormatGenerators.push(() => maxBet.toFixed(1));
    }
    maxBetFormatGenerators.push(() => maxBet.toFixed(2));

    // Deduplicate formats generated by functions while preserving order
    const getUniqueOrderedStrings = (generators: (() => string)[]) : string[] => {
        const seen = new Set<string>();
        const result: string[] = [];
        for (const gen of generators) {
            const str = gen();
            if (!seen.has(str)) {
                seen.add(str);
                result.push(str);
            }
        }
        return result;
    };

    const uniqueMinBetFormats = getUniqueOrderedStrings(minBetFormatGenerators);
    const uniqueMaxBetFormats = getUniqueOrderedStrings(maxBetFormatGenerators);

    // Prioritize minBet's full display by iterating through its formats first
    for (const fMin of uniqueMinBetFormats) {
        for (const fMax of uniqueMaxBetFormats) {
            if (checkLength(fMin, fMax)) {
                return { formattedMinBet: fMin, formattedMaxBet: fMax };
            }
        }
    }

    // Fallback: if absolutely nothing fits (highly unlikely with abbreviations included),
    // return the most abbreviated forms. This matches the original function's final fallback.
    return { formattedMinBet: abbreviateNumber(minBet), formattedMaxBet: abbreviateNumber(maxBet) };
};